import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Star, ArrowLeft, Sparkles, Heart } from 'lucide-react'
import { Seo } from '../../components/Seo'

interface LuckyNumberResult {
  numbers: number[]
  reason: string
  zodiac_info: string
  birthdate_sum: number
}

const zodiacSigns = [
  '양자리', '황소자리', '쌍둥이자리', '게자리', '사자자리', '처녀자리',
  '천칭자리', '전갈자리', '사수자리', '염소자리', '물병자리', '물고기자리'
]

const LuckyNumberPage = () => {
  const [birthdate, setBirthdate] = useState('')
  const [zodiac, setZodiac] = useState('')
  const [luckyNumber, setLuckyNumber] = useState<LuckyNumberResult | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  const resetForm = () => {
    setBirthdate('')
    setZodiac('')
    setLuckyNumber(null)
  }

  const generateLuckyNumbers = () => {
    if (!birthdate) {
      alert('생년월일을 입력해주세요')
      return
    }

    setIsGenerating(true)
    
    // Simulate generation delay for effect
    setTimeout(() => {
      const birthdateDigits = birthdate.replace(/-/g, '').split('').map(Number)
      const birthdateSum = birthdateDigits.reduce((a, b) => a + b, 0)
      
      // Generate 6 unique numbers based on birthdate and zodiac
      const luckyNums: number[] = []
      const zodiacIndex = zodiacSigns.indexOf(zodiac) + 1
      
      // Use birthdate components
      const year = parseInt(birthdate.substring(2, 4))
      const month = parseInt(birthdate.substring(5, 7))
      const day = parseInt(birthdate.substring(8, 10))
      
      // Generate numbers using various methods
      luckyNums.push(Math.max(1, Math.min(45, (birthdateSum % 45) || 1)))
      luckyNums.push(Math.max(1, Math.min(45, (year % 45) || 2)))
      luckyNums.push(Math.max(1, Math.min(45, (month * 3) % 45 || 3)))
      luckyNums.push(Math.max(1, Math.min(45, (day % 45) || 4)))
      luckyNums.push(Math.max(1, Math.min(45, ((zodiacIndex * 3 + day) % 45) || 5)))
      luckyNums.push(Math.max(1, Math.min(45, ((birthdateSum + zodiacIndex) % 45) || 6)))
      
      // Ensure uniqueness
      const uniqueNums = Array.from(new Set(luckyNums))
      while (uniqueNums.length < 6) {
        const newNum = Math.floor(Math.random() * 45) + 1
        if (!uniqueNums.includes(newNum)) {
          uniqueNums.push(newNum)
        }
      }
      
      // Sort numbers
      uniqueNums.sort((a, b) => a - b)
      
      setLuckyNumber({
        numbers: uniqueNums.slice(0, 6),
        reason: `생년월일과 ${zodiac}의 별자리 에너지를 조합하여 생성된 특별한 번호입니다`,
        zodiac_info: `${zodiac}는 행운의 숫자 ${zodiacIndex * 3}과 연결되어 있습니다`,
        birthdate_sum: birthdateSum
      })
      
      setIsGenerating(false)
    }, 2000)
  }

  const getLotteryNumberColor = (num: number) => {
    if (num <= 10) return 'from-yellow-400 to-yellow-600'
    if (num <= 20) return 'from-blue-400 to-blue-600'
    if (num <= 30) return 'from-red-400 to-red-600'
    if (num <= 40) return 'from-gray-400 to-gray-600'
    return 'from-green-400 to-green-600'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="나만의 수호신 번호"
        description="생년월일과 별자리를 바탕으로 개인화된 행운의 번호를 생성해 보세요."
        canonicalPath="/analysis/lucky-number"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-yellow-500 via-amber-500 to-yellow-600 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/analysis" className="inline-flex items-center text-white hover:text-white/80 mb-6 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            분석 방법으로 돌아가기
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-4">
              <Star className="w-12 h-12 text-white mr-4" />
              <h1 className="text-4xl md:text-6xl font-black text-white">
                나만의 수호신 번호
              </h1>
            </div>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              생년월일과 별자리를 바탕으로 당신만의 특별한 행운의 번호를 생성하세요
            </p>
          </motion.div>
        </div>
      </section>

      {/* Input Form */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-8 shadow-xl"
          >
            <div className="flex items-center justify-center mb-8">
              <Sparkles className="w-8 h-8 text-yellow-500 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900">정보 입력</h2>
            </div>

            <div className="space-y-6">
              {/* Birthdate Input */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  생년월일
                </label>
                <input
                  type="date"
                  value={birthdate}
                  onChange={(e) => setBirthdate(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition-colors"
                  placeholder="YYYY-MM-DD"
                />
              </div>

              {/* Zodiac Selection */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  별자리
                </label>
                <select
                  value={zodiac}
                  onChange={(e) => setZodiac(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition-colors"
                >
                  <option value="">별자리를 선택하세요</option>
                  {zodiacSigns.map((sign) => (
                    <option key={sign} value={sign}>
                      {sign}
                    </option>
                  ))}
                </select>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateLuckyNumbers}
                disabled={isGenerating || !birthdate || !zodiac}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
                  isGenerating || !birthdate || !zodiac
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white hover:shadow-lg hover:scale-105'
                }`}
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    행운의 번호 생성 중...
                  </span>
                ) : (
                  '행운의 번호 생성하기'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lucky Numbers Result */}
      {luckyNumber && (
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-8 shadow-2xl text-white mb-8"
            >
              <div className="flex items-center justify-center mb-8">
                <Heart className="w-10 h-10 mr-3" />
                <h2 className="text-3xl font-black">당신의 수호신 번호</h2>
              </div>

              {/* Lucky Numbers Display */}
              <div className="flex justify-center items-center gap-3 mb-8 flex-wrap">
                {luckyNumber.numbers.map((num, index) => (
                  <motion.div
                    key={num}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br ${getLotteryNumberColor(num)} flex items-center justify-center text-white font-black text-2xl md:text-3xl shadow-lg`}
                  >
                    {num}
                  </motion.div>
                ))}
              </div>

              {/* Explanation */}
              <div className="space-y-4 bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <div>
                  <h3 className="font-bold text-lg mb-2">✨ 번호의 의미</h3>
                  <p className="text-white/90">{luckyNumber.reason}</p>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">🌟 별자리 연결</h3>
                  <p className="text-white/90">{luckyNumber.zodiac_info}</p>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">🎂 생년월일 에너지</h3>
                  <p className="text-white/90">
                    생년월일의 숫자 합: {luckyNumber.birthdate_sum} - 이 에너지가 당신의 행운 번호에 반영되었습니다
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-xl p-6 shadow-lg text-center"
              >
                <div className="text-3xl mb-2">🎯</div>
                <h4 className="font-bold text-gray-900 mb-2">개인화된 번호</h4>
                <p className="text-sm text-gray-600">
                  당신만의 특별한 정보로 만들어진 유일무이한 번호입니다
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white rounded-xl p-6 shadow-lg text-center"
              >
                <div className="text-3xl mb-2">💫</div>
                <h4 className="font-bold text-gray-900 mb-2">우주의 에너지</h4>
                <p className="text-sm text-gray-600">
                  별자리와 생년월일의 에너지를 담아 만들어졌습니다
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white rounded-xl p-6 shadow-lg text-center"
              >
                <div className="text-3xl mb-2">🍀</div>
                <h4 className="font-bold text-gray-900 mb-2">행운의 기원</h4>
                <p className="text-sm text-gray-600">
                  당신의 행운을 기원하는 마음을 담았습니다
                </p>
              </motion.div>
            </div>

            {/* Regenerate Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center flex gap-4 justify-center"
            >
              <button
                onClick={resetForm}
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-8 rounded-full hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                다시 생성하기
              </button>
            </motion.div>

            {/* Disclaimer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-12 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg"
            >
              <p className="text-gray-700">
                <strong className="text-yellow-700">⚠️ 안내:</strong> 이 기능은 재미와 흥미를 위한 것으로, 
                실제 당첨 확률을 높이는 것은 아닙니다. 
                로또는 무작위 추첨이므로 모든 번호는 동일한 당첨 확률을 가집니다.
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      {!luckyNumber && (
        <section className="py-12 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                어떻게 작동하나요?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6">
                  <div className="text-2xl mb-3">1️⃣</div>
                  <h4 className="font-bold text-gray-900 mb-2">생년월일 분석</h4>
                  <p className="text-gray-700 text-sm">
                    입력한 생년월일의 각 숫자를 분석하여 당신만의 고유한 에너지를 파악합니다
                  </p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6">
                  <div className="text-2xl mb-3">2️⃣</div>
                  <h4 className="font-bold text-gray-900 mb-2">별자리 에너지</h4>
                  <p className="text-gray-700 text-sm">
                    선택한 별자리의 행운의 숫자와 에너지를 번호 생성에 반영합니다
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6">
                  <div className="text-2xl mb-3">3️⃣</div>
                  <h4 className="font-bold text-gray-900 mb-2">조합 알고리즘</h4>
                  <p className="text-gray-700 text-sm">
                    생년월일과 별자리 정보를 독특한 알고리즘으로 조합하여 6개의 번호를 생성합니다
                  </p>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-6">
                  <div className="text-2xl mb-3">4️⃣</div>
                  <h4 className="font-bold text-gray-900 mb-2">행운의 번호 완성</h4>
                  <p className="text-gray-700 text-sm">
                    당신만을 위한 특별한 수호신 번호가 완성됩니다
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  )
}

export default LuckyNumberPage

